import React, { Component, Fragment } from 'react'
import { inject, observer } from 'mobx-react'
import styled, { keyframes, css } from 'styled-components'
import PropTypes from 'prop-types'

import downCarrotImage from '~/public/static/images/svg/downCarrot.svg'
import blueCheckImage from '~/public/static/images/svg/blueCheck.svg'
import Spacer from '~/components/Spacer'

import { NEW_YORK_STATE_LICENSE_COLUMNS } from '~/components/traffic/lookup/Forms/Elements/searchConfig'
import withTranslations from '~/components/_hocs/withTranslations'

const Container = styled.div`
  position: relative;
  display: inline-block;
  ${'' /* padding: 0 10px 5px 0; */}
  height: 21px;
  width: 21px;
`

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 rgba(48, 137, 243, 0.5);
  }
  100% {
    box-shadow: 0 0 0 26px rgba(48, 137, 243, 0);
  }
`

const Icon = styled.button.attrs({ type: 'button' })`
  left: 0;
  top: 0;
  border-radius: 50%;
  height: 100%;
  width: 100%;
  border: 0;
  padding: 0;
  margin: 0;
  background-color: #e1e1e6;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
  outline: none;
  transform: rotate(${props => (props.open ? 180 : 0)}deg);
  transition: all 0.5s ease;
  ${props =>
    props.wasTouched
      ? null
      : css`
          animation: ${pulseAnimation} 2s infinite;
        `}
`

const DropContainer = styled.div`
  position: absolute;
  z-index: 2;
  top: 27px;
  right: 0px;
  width: 240px;
  display: ${props => (props.open ? 'block' : 'none')};
`

const DropContent = styled.div`
  ${'' /* margin: 27px 0 0 0; */}
  position: relative;
  background: white;
  width: 100%;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  padding: 19px 15px;
  border: 0px solid
    ${props =>
      props.error
        ? props.theme.colors.error
        : props.theme.colors.ui.inputBorder};
`

const Option = styled.button.attrs({ type: 'button' })`
  display: flex;
  flex-direction: row;
  padding: 0;
  margin: 0;
  border: none;
  font-family: ${props => props.theme.fonts.avenirNextMedium};
  color: ${props =>
    props.isActive
      ? props.theme.colors.primary
      : props.theme.colors.text.input};
  cursor: ${props => (props.isActive ? 'default' : 'pointer')};
  font-size: 0.825rem;
  background: white;
  :hover {
    color: ${props => props.theme.colors.primary};
  }
`

const CheckContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 18px;
  height: 18px;
`

const NameContainer = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  flex-direction: row;
  padding: 0 0 0 5px;
`

@inject(all => ({
  isMobile: all.store.ui.isMobile,
  setColumn: all.store.trafficLookup.setColumn,
  columnOne: all.store.trafficLookup.columnOne,
  columnTwo: all.store.trafficLookup.columnTwo,
  isNYSLicense: all.store.trafficLookup.isNYSLicense,
  searchUsingOtherInfo: all.store.trafficLookup.searchUsingOtherInfo,
  touchedSearchUsingOtherInfo:
    all.store.trafficLookup.touchedSearchUsingOtherInfo,
  searchUsingOtherInfoTouched:
    all.store.trafficLookup.searchUsingOtherInfoTouched
}))
@observer
class DropdownMenu extends Component {
  constructor(props) {
    super(props)
    this.state = {
      open: false
    }
  }

  get currentColumnName() {
    return this.props.columnNumber === 'one' ? 'columnOne' : 'columnTwo'
  }

  changeOpen = value => {
    if (value) {
      this.props.touchedSearchUsingOtherInfo()
    }
    this.setState({
      open: value
    })
  }

  handleClick = e => {
    const currentPostion = this.state.open
    this.changeOpen(!currentPostion)
  }

  handleLinkClick = option => e => {
    e.preventDefault()
    this.changeOpen(false)
    this.props.setColumn(this.currentColumnName, option)
  }

  render() {
    if (
      !this.props.isNYSLicense ||
      (this.props.isNYSLicense && !this.props.searchUsingOtherInfo)
    ) {
      return <div />
    }
    const buttonActions = {}
    buttonActions.onClick = () => this.changeOpen(!this.state.open) // desktop
    // buttonActions.onTouchStart = () => this.changeOpen(!this.state.open) // mobile

    const containerActions = {}
    containerActions.onMouseLeave = () => this.changeOpen(false) //

    const columnOptions =
      NEW_YORK_STATE_LICENSE_COLUMNS[this.props.columnNumber]
    return (
      <Container>
        <Icon
          {...buttonActions}
          open={this.state.open}
          tabIndex="-1"
          wasTouched={this.props.searchUsingOtherInfoTouched}
        >
          <img alt="down arrow carrot" src={downCarrotImage} />
        </Icon>
        <DropContainer open={this.state.open} {...containerActions}>
          <DropContent>
            {columnOptions.map((option, index) => {
              const includeSpacer = columnOptions.length - 1 !== index
              const showCheck = this.props[this.currentColumnName] === option
              return (
                <Fragment key={option}>
                  <Option
                    onClick={this.handleLinkClick(option)}
                    isActive={showCheck}
                    tabIndex="-1"
                  >
                    <CheckContainer>
                      {showCheck ? (
                        <img src={blueCheckImage} alt="Check mark" />
                      ) : null}
                    </CheckContainer>
                    <NameContainer>
                      {this.props.translate(`${option}.label`)}
                    </NameContainer>
                  </Option>
                  {includeSpacer ? <Spacer mobile={25} desktop={25} /> : null}
                </Fragment>
              )
            })}
          </DropContent>
        </DropContainer>
      </Container>
    )
  }
}

DropdownMenu.defaultProps = {}

DropdownMenu.propTypes = {
  columnNumber: PropTypes.oneOf(['one', 'two']).isRequired
}

export default withTranslations(DropdownMenu, 'form.traffic')
